import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { Modal } from '../../../components/ui/Modal';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiRefreshCw, FiDollarSign, FiAlertCircle, FiFileText } from 'react-icons/fi';
import {
  fetchInvoices,
  addInvoice,
  revertInvoice,
  Invoice,
  fetchTransactions,
} from '../service/financeService';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  background-color: ${theme.colors.white};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.light.text.primary};
  }

  &.revert:hover {
    color: ${theme.colors.accent.warning};
    background-color: ${theme.colors.accent.warning}20;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.light.text.secondary};
`;

const AmountCell = styled.div`
  text-align: right;
  color: ${theme.colors.light.text.primary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ status }) => {
    switch (status) {
      case 'received':
      case 'paid':
        return theme.colors.accent.success + '20';
      case 'pending':
        return theme.colors.accent.warning + '20';
      case 'overdue':
        return theme.colors.accent.danger + '20';
      default:
        return theme.colors.light.background.tertiary;
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'received':
      case 'paid':
        return theme.colors.accent.success;
      case 'pending':
        return theme.colors.accent.warning;
      case 'overdue':
        return theme.colors.accent.danger;
      default:
        return theme.colors.light.text.secondary;
    }
  }};
`;

const InvoiceRevenuePage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  
  // Check if user is a company owner/admin (logged in from companies, not staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail || 
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check permissions for invoice
  const isStaffUser = !!staffData && !isCompanyOwner;
  const canEditInvoice = isCompanyOwner || (isStaffUser && staffData?.permissions?.invoice === 'edit');

  const [invoiceReceivedAmounts, setInvoiceReceivedAmounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [invoiceToRevert, setInvoiceToRevert] = useState<Invoice | null>(null);
  const [revertReason, setRevertReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    clientName: '',
    clientType: 'Individual' as 'Individual' | 'Factory' | 'Bank',
    description: '',
    status: 'received' as 'received' | 'pending' | 'overdue',
    paymentDate: '',
    paymentMethod: 'cash',
  });

  const expectedCompanyId = '';
  const companyId = companyData?.id || expectedCompanyId;


  useEffect(() => {
    if (user) {
      loadInvoices();
    }
  }, [user, companyData]);

  useEffect(() => {
    if (invoices.length > 0) {
      loadReceivedAmounts();
    }
  }, [invoices]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const data = await fetchInvoices(companyId);
      setInvoices(data);
    } catch (error: any) {
      console.error('Error loading invoices:', error);
      toast.error(error.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadReceivedAmounts = async () => {
    try {
      // First, use paidAmount from invoices if available
      const amountsFromInvoices: Record<string, number> = {};
      invoices.forEach((invoice) => {
        if (invoice.paidAmount !== undefined && invoice.paidAmount > 0) {
          amountsFromInvoices[invoice.invoiceNumber] = invoice.paidAmount;
        }
      });

      // Also fetch transactions to calculate received amounts (for invoices without paidAmount field)
      const { transactions } = await fetchTransactions(companyId, {
        type: 'invoice',
        timeFilter: 'all',
        pageSize: 1000, // Get all transactions
      });

      // Group transactions by invoice number (ref field) and sum debit amounts
      // Start with amounts from invoices (paidAmount field)
      const receivedAmounts: Record<string, number> = { ...amountsFromInvoices };
      
      // For invoices without paidAmount, calculate from transactions
      transactions.forEach((transaction) => {
        if (transaction.ref && transaction.debit > 0) {
          // Skip revert transactions (they have -REVERT suffix)
          if (!transaction.ref.includes('-REVERT')) {
            // Only calculate from transactions if invoice doesn't have paidAmount
            // Otherwise, use the paidAmount from invoice (more accurate)
            if (!amountsFromInvoices[transaction.ref]) {
              receivedAmounts[transaction.ref] = (receivedAmounts[transaction.ref] || 0) + transaction.debit;
            }
          }
        }
      });

      setInvoiceReceivedAmounts(receivedAmounts);
    } catch (error: any) {
      console.error('Error loading received amounts:', error);
      // Fallback: use paidAmount from invoices
      const amountsFromInvoices: Record<string, number> = {};
      invoices.forEach((invoice) => {
        if (invoice.paidAmount !== undefined && invoice.paidAmount > 0) {
          amountsFromInvoices[invoice.invoiceNumber] = invoice.paidAmount;
        }
      });
      setInvoiceReceivedAmounts(amountsFromInvoices);
    }
  };

  const totalInvoices = invoices.reduce((sum, i) => sum + i.amount, 0);
  const totalReceived = invoices.reduce((sum, i) => {
    const received = invoiceReceivedAmounts[i.invoiceNumber] || 0;
    return sum + received;
  }, 0);
  const receivedInvoices = invoices.filter((i) => i.status === 'received').reduce((sum, i) => sum + i.amount, 0);
  const pendingInvoices = invoices.filter((i) => i.status === 'pending' || i.status === 'overdue').reduce((sum, i) => sum + i.amount, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await addInvoice(companyId, {
        invoiceNumber: formData.invoiceNumber || undefined,
        date: new Date(formData.date),
        amount: parseFloat(formData.amount),
        clientName: formData.clientName,
        clientType: formData.clientType,
        description: formData.description,
        status: 'received',
      });

      toast.success('Invoice added successfully');
      setShowInvoiceModal(false);
      setFormData({
        invoiceNumber: '',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        clientName: '',
        clientType: 'Individual',
        description: '',
        status: 'received',
        paymentDate: '',
        paymentMethod: 'cash',
      });
      await loadInvoices();
      await loadReceivedAmounts();
    } catch (error: any) {
      console.error('Error adding invoice:', error);
      toast.error(error.message || 'Failed to add invoice');
    } finally {
      setSaving(false);
    }
  };


  const handleRevert = async () => {
    if (!invoiceToRevert || !revertReason.trim()) {
      toast.error('Please provide a reason for reverting');
      return;
    }

    try {
      await revertInvoice(companyId, invoiceToRevert.id, revertReason.trim());
      toast.success('Invoice reverted successfully');
      setShowRevertModal(false);
      setInvoiceToRevert(null);
      setRevertReason('');
      await loadInvoices();
      await loadReceivedAmounts();
    } catch (error: any) {
      console.error('Error reverting invoice:', error);
      toast.error(error.message || 'Failed to revert invoice');
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | any): string => {
    if (!date) return '—';
    const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Invoice Revenue</PageTitle>
        {canEditInvoice && (
          <Button onClick={() => setShowInvoiceModal(true)}>
            <FiPlus size={18} style={{ marginRight: theme.spacing[2] }} />
            Add Invoice
          </Button>
        )}
      </PageHeader>

      <StatsContainer>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.primary[600]}>
              {formatCurrency(totalInvoices)}
            </StatValue>
            <StatLabel>Total Invoices</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.success}>
              {formatCurrency(totalReceived)}
            </StatValue>
            <StatLabel>Total Received</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.warning}>
              {formatCurrency(pendingInvoices)}
            </StatValue>
            <StatLabel>Pending</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue>{invoices.length}</StatValue>
            <StatLabel>Total Entries</StatLabel>
          </CardBody>
        </StatCard>
      </StatsContainer>

      <Card>
        <CardBody>
          {loading ? (
            <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
              Loading invoices...
            </div>
          ) : invoices.length > 0 ? (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Invoice #</Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Client Name</Table.HeaderCell>
                  <Table.HeaderCell>Client Type</Table.HeaderCell>
                  <Table.HeaderCell>Description</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'right' }}>Total Amount</Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'right' }}>Received Amount</Table.HeaderCell>
                  {canEditInvoice && <Table.HeaderCell>Actions</Table.HeaderCell>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {invoices.map((invoice) => (
                  <Table.Row key={invoice.id}>
                    <Table.Cell>{invoice.invoiceNumber}</Table.Cell>
                    <Table.Cell>{formatDate(invoice.date)}</Table.Cell>
                    <Table.Cell>{invoice.clientName}</Table.Cell>
                    <Table.Cell>{invoice.clientType || '—'}</Table.Cell>
                    <Table.Cell>{invoice.description || '—'}</Table.Cell>
                    <Table.Cell>
                      <StatusBadge status={invoice.status}>{invoice.status}</StatusBadge>
                    </Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      <AmountCell>{formatCurrency(invoice.amount)}</AmountCell>
                    </Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      <AmountCell>
                        {formatCurrency(invoiceReceivedAmounts[invoice.invoiceNumber] || 0)}
                      </AmountCell>
                    </Table.Cell>
                    {canEditInvoice && (
                      <Table.Cell>
                        <div style={{ display: 'flex', gap: theme.spacing[1] }}>
                          {!invoice.reverted && (
                            <ActionButton
                              className="revert"
                              onClick={() => {
                                setInvoiceToRevert(invoice);
                                setShowRevertModal(true);
                              }}
                              title="Revert"
                            >
                              <FiRefreshCw size={18} />
                            </ActionButton>
                          )}
                          {invoice.reverted && (
                            <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                              Reverted
                            </span>
                          )}
                        </div>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <EmptyState>
              <FiFileText size={48} style={{ marginBottom: theme.spacing[2], opacity: 0.5 }} />
              <p>No invoices found. Add your first invoice to get started.</p>
            </EmptyState>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setFormData({
        invoiceNumber: '',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        clientName: '',
        clientType: 'Individual',
        description: '',
        status: 'received',
        paymentDate: '',
        paymentMethod: 'cash',
          });
        }}
        title="Add Invoice"
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowInvoiceModal(false);
                setFormData({
        invoiceNumber: '',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        clientName: '',
        clientType: 'Individual',
        description: '',
        status: 'received',
        paymentDate: '',
        paymentMethod: 'cash',
                });
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const form = document.getElementById('invoice-form') as HTMLFormElement;
                if (form) {
                  form.requestSubmit();
                }
              }}
              loading={saving}
              disabled={saving}
            >
              Add Invoice
            </Button>
          </>
        }
      >
        <form id="invoice-form" onSubmit={handleSubmit}>
          <ModalContent>
            <FormRow>
              <FormGroup>
                <Label>Invoice Number</Label>
                <Input
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                  placeholder="Auto-generated if left empty"
                  dark
                />
              </FormGroup>
              <FormGroup>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  dark
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Amount *</Label>
                <Input
                  type="text"
                  value={formData.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setFormData({ ...formData, amount: value });
                    }
                  }}
                  placeholder="0.00"
                  required
                  dark
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Client Name *</Label>
                <Input
                  value={formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  placeholder="Client name"
                  required
                  dark
                />
              </FormGroup>
              <FormGroup>
                <Label>Client Type</Label>
                <Select
                  value={formData.clientType}
                  onChange={(e) => setFormData({ ...formData, clientType: e.target.value as 'Individual' | 'Factory' | 'Bank' })}
                >
                  <option value="Individual">Individual</option>
                  <option value="Factory">Factory</option>
                  <option value="Bank">Bank</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Invoice description"
                required
                dark
              />
            </FormGroup>
          </ModalContent>
        </form>
      </Modal>

      <Modal
        isOpen={showRevertModal}
        onClose={() => {
          setShowRevertModal(false);
          setInvoiceToRevert(null);
          setRevertReason('');
        }}
        title="Revert Invoice"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowRevertModal(false);
                setInvoiceToRevert(null);
                setRevertReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleRevert}
              disabled={!revertReason.trim()}
            >
              Revert
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3], marginBottom: theme.spacing[4] }}>
          <FiAlertCircle size={24} style={{ color: theme.colors.accent.warning }} />
          <div>
            <p>Are you sure you want to revert this invoice? This will create a credit transaction to reverse the original debit.</p>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary, marginTop: theme.spacing[2] }}>
              Invoice: {invoiceToRevert?.invoiceNumber}
            </p>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
              Amount: {invoiceToRevert ? formatCurrency(invoiceToRevert.amount) : ''}
            </p>
          </div>
        </div>
        <div>
          <Label>Reason for Revert *</Label>
          <Input
            value={revertReason}
            onChange={(e) => setRevertReason(e.target.value)}
            placeholder="Enter reason for reverting this invoice"
            required
            dark
          />
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default InvoiceRevenuePage;
